/**
 * KEEPING MOTION HONEST, AND CHEAP.
 *
 * Two jobs, and they are the same job seen from two sides. This product's
 * animations are all claims — an orbit means "the record says a segment ran
 * recently", a pulse means "a request is outstanding". A claim that keeps being
 * made while nobody is reading it is not a claim any more, it is a battery
 * drain; and on a phone in a pocket that is the difference between a workspace
 * that is open all day and one that is not.
 *
 * THE MECHANISM IS ONE ATTRIBUTE ON THE DOCUMENT, NOT A TIMER PER VIEW. Every
 * decorative animation in `app.css` is written to stop when the document
 * carries `data-motion="paused"`, so pausing is one property change rather than
 * thirteen components each remembering to listen. It also means a view added
 * later inherits the behaviour by writing its animation the way the others are
 * written, instead of by remembering to opt in.
 *
 * IT PAUSES RATHER THAN REMOVES, AND THE DIFFERENCE MATTERS HERE. Removing an
 * animation mid-flight can change what is on screen — a rotating orbit's
 * transform resets to its base — which would make a hidden tab's content
 * different from a visible one's. `animation-play-state: paused` freezes the
 * paint exactly where it was, so the tab that comes back shows the frame it
 * left.
 */

/** The attribute `app.css` keys every decorative animation off. Named once, so
 *  a specimen can assert the stylesheet and this file agree about the string. */
export const MOTION_ATTRIBUTE = 'data-motion';
export const MOTION_PAUSED = 'paused';
export const MOTION_RUNNING = 'running';

/**
 * WATCH THE DOCUMENT'S VISIBILITY, AND SAY SO ON THE ROOT ELEMENT.
 *
 * `document.hidden` IS READ RATHER THAN ASSUMED FALSE. A workspace that mounts
 * into a background tab — which is what "open in a new tab" does — would
 * otherwise start its animations running and only pause them at the first
 * visibility event, which in a background tab may be minutes away.
 *
 * THE LISTENER IS ON `visibilitychange` AND NOT ON `blur`. A window that has
 * lost focus is still on screen; the Owner is reading it while typing somewhere
 * else, and freezing the mark under their eyes would be the animation lying
 * about the one thing it exists to say.
 */
export function watchHiddenTab(doc: Document = document): () => void {
	const apply = (): void => {
		doc.documentElement.setAttribute(
			MOTION_ATTRIBUTE,
			doc.hidden ? MOTION_PAUSED : MOTION_RUNNING,
		);
	};
	apply();
	doc.addEventListener('visibilitychange', apply);
	return () => {
		doc.removeEventListener('visibilitychange', apply);
	};
}

/**
 * WHETHER A REFRESH IS DUE, GIVEN THE TAB'S VISIBILITY.
 *
 * The Observatory already re-reads a running window every thirty seconds while
 * the tab is visible. This is the same rule stated once, for anything that
 * polls: THERE IS NO POINT ASKING A QUESTION NOBODY IS WAITING FOR AN ANSWER
 * TO. A background tab that keeps polling is a Worker invocation every thirty
 * seconds for a screen nobody is looking at, and the moment the Owner comes
 * back there is a `visibilitychange` that triggers a read anyway.
 */
export function shouldPoll(doc: Document = document): boolean {
	return !doc.hidden;
}
